import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Copy, Check, X } from "lucide-react";

/**
 * Admin → Reset a user's password.
 * Two modes:
 *   - Auto-generate a 12-char temp password (default)
 *   - Set a custom password (>=8 chars)
 * Server revokes refresh tokens & sends an in-app notification to the user.
 */
export default function AdminResetPasswordButton({ userId, username, role, testId }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("auto");      // 'auto' | 'manual'
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Server blocks resetting another admin; hide the button entirely for admins
  if (role === "admin") return null;

  const reset = async () => {
    if (mode === "manual" && pw.trim().length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const body = mode === "manual" ? { new_password: pw.trim() } : {};
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, body);
      if (data?.auto_generated) {
        setResult({ password: data.temp_password, generated: true });
      } else {
        setResult({ password: pw.trim(), generated: false });
      }
      toast.success(`Password reset for ${username}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setMode("auto");
    setPw("");
    setResult(null);
    setCopied(false);
  };

  const copyPw = async () => {
    if (!result?.password) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Select & copy manually.");
    }
  };

  return (
    <>
      <button
        data-testid={testId || `reset-pw-${userId}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors"
      >
        <KeyRound className="w-3 h-3" /> Reset PW
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          data-testid="reset-pw-modal"
        >
          <div className="w-full max-w-md bg-[#1A1A1E] border border-[#2A2A30] p-6 relative">
            <button onClick={close} className="absolute top-3 right-3 text-[#71717A] hover:text-white" data-testid="reset-pw-close">
              <X className="w-5 h-5" />
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F59E0B] mb-1">// reset password</div>
            <h3 className="font-heading text-2xl uppercase tracking-wider text-white">{username}</h3>

            {!result ? (
              <>
                <div className="space-y-3 mt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={mode === "auto"}
                      onChange={() => setMode("auto")}
                      data-testid="reset-pw-mode-auto"
                      className="accent-[#F59E0B]"
                    />
                    <span className="font-mono text-xs text-white">Auto-generate temporary password (12 chars)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={mode === "manual"}
                      onChange={() => setMode("manual")}
                      data-testid="reset-pw-mode-manual"
                      className="accent-[#F59E0B]"
                    />
                    <span className="font-mono text-xs text-white">Set custom password</span>
                  </label>
                  {mode === "manual" && (
                    <input
                      type="text"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="Enter new password (min 8 chars)"
                      autoFocus
                      data-testid="reset-pw-input"
                      className="w-full bg-[#0F0F18] border border-[#2A2A30] px-3 py-2 font-mono text-sm text-white focus:border-[#F59E0B] outline-none"
                    />
                  )}
                </div>
                <div className="mt-4 text-[11px] text-[#A1A1AA] font-mono leading-relaxed border-l-2 border-[#F59E0B]/40 pl-3">
                  This will invalidate the user's active sessions and notify them in-app. Hand off the new password securely.
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    onClick={close}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-white px-3 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={busy}
                    onClick={reset}
                    data-testid="reset-pw-confirm"
                    className="bg-[#F59E0B] text-black font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 hover:bg-[#FBBF24] disabled:opacity-50"
                  >
                    {busy ? "Resetting…" : "Reset Password"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mt-5 border border-[#10B981] bg-[#10B981]/10 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#10B981] mb-2">// new password</div>
                  <div className="flex items-center gap-2">
                    <code data-testid="reset-pw-result" className="flex-1 font-mono text-base text-white bg-[#09090B] px-3 py-2 break-all select-all">
                      {result.password}
                    </code>
                    <button
                      onClick={copyPw}
                      data-testid="reset-pw-copy"
                      className="flex items-center gap-1 border border-[#10B981] text-[#10B981] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:bg-[#10B981]/10"
                    >
                      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </button>
                  </div>
                  <div className="mt-3 text-[11px] text-[#A1A1AA] font-mono leading-relaxed">
                    Save this now — it won't be shown again. The user has been notified in-app.
                  </div>
                </div>
                <div className="mt-5 flex justify-end">
                  <button
                    onClick={close}
                    data-testid="reset-pw-done"
                    className="bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 hover:bg-[#6D28D9]"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
